import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Calendar as CalIcon, ChevronLeft, ChevronRight, RefreshCw,
  MapPin, User, Clock, AlertTriangle, Plus
} from 'lucide-react';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  new:         'bg-slate-200 border-slate-300 text-slate-700',
  assigned:    'bg-blue-100 border-blue-300 text-blue-800',
  en_route:    'bg-purple-100 border-purple-300 text-purple-800',
  in_progress: 'bg-amber-100 border-amber-300 text-amber-800',
  completed:   'bg-emerald-100 border-emerald-300 text-emerald-800',
  verified:    'bg-teal-100 border-teal-300 text-teal-800',
  cancelled:   'bg-red-100 border-red-300 text-red-700',
};

const PRIORITY_DOTS = {
  low: 'bg-gray-400',
  normal: 'bg-blue-400',
  high: 'bg-orange-400',
  urgent: 'bg-red-500',
};

/* ── Calendar Helpers ─────────────────────────────────────────────── */
function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year, month) {
  return new Date(year, month, 1).getDay();
}

function formatMonth(year, month) {
  return new Date(year, month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function toDateStr(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/* ── Job Pill (on calendar) ───────────────────────────────────────── */
function JobPill({ job, onClick }) {
  const sc = STATUS_COLORS[job.status] || STATUS_COLORS.new;
  const pd = PRIORITY_DOTS[job.priority] || PRIORITY_DOTS.normal;
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(job); }}
      className={cn("w-full text-left px-1.5 py-0.5 rounded text-[10px] leading-tight border truncate flex items-center gap-1 hover:opacity-80 transition-opacity", sc)}
      title={`${job.title} — ${job.assigned_name || 'Unassigned'} — ${job.status}`}
      data-testid={`cal-job-${job.id}`}
    >
      <span className={cn("w-1.5 h-1.5 rounded-full flex-shrink-0", pd)} />
      <span className="truncate">{job.scheduled_time ? `${job.scheduled_time} ` : ''}{job.title}</span>
    </button>
  );
}

/* ── Job Detail Sidebar ───────────────────────────────────────────── */
function DayDetail({ date, jobs, onClose, onJobClick }) {
  if (!date) return null;
  const dayJobs = jobs.filter(j => j.scheduled_date === date);
  const d = new Date(date + 'T00:00:00');
  const formatted = d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <Card className="bg-white border-gray-border" data-testid="day-detail">
      <CardHeader className="pb-2 flex-row items-center justify-between">
        <CardTitle className="text-sm font-medium">{formatted}</CardTitle>
        <Badge variant="outline" className="text-[10px]">{dayJobs.length} job{dayJobs.length !== 1 ? 's' : ''}</Badge>
      </CardHeader>
      <CardContent className="space-y-2">
        {dayJobs.length === 0 && <p className="text-xs text-gray-400 py-3 text-center">No jobs scheduled</p>}
        {dayJobs.sort((a, b) => (a.scheduled_time || '').localeCompare(b.scheduled_time || '')).map(job => (
          <div
            key={job.id}
            onClick={() => onJobClick(job)}
            className="p-2.5 rounded-lg border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all cursor-pointer"
          >
            <div className="flex items-start justify-between gap-2 mb-1">
              <p className="text-sm font-medium text-text-primary leading-tight">{job.title}</p>
              <Badge className={cn("text-[10px] flex-shrink-0", STATUS_COLORS[job.status])}>{job.status}</Badge>
            </div>
            <div className="flex flex-wrap gap-2 text-[11px] text-gray-400">
              {job.scheduled_time && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{job.scheduled_time}</span>}
              {job.assigned_name && <span className="flex items-center gap-1"><User className="h-3 w-3" />{job.assigned_name}</span>}
              {job.address && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{job.address}</span>}
            </div>
            {job.priority === 'urgent' && (
              <div className="flex items-center gap-1 text-[10px] text-red-500 mt-1">
                <AlertTriangle className="h-3 w-3" />Urgent
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/* ── Main Page ────────────────────────────────────────────────────── */
export default function FieldSchedulePage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth());
  const [selectedDate, setSelectedDate] = useState(null);

  const fetchSchedule = useCallback(async () => {
    setLoading(true);
    try {
      const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${getDaysInMonth(year, month)}`;
      const res = await axios.get(`${API}/api/field/schedule`, { params: { start_date: startDate, end_date: endDate } });
      setJobs(res.data);
    } catch (e) {
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };
  const goToday = () => {
    const now = new Date();
    setYear(now.getFullYear());
    setMonth(now.getMonth());
    setSelectedDate(now.toISOString().slice(0, 10));
  };

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);
  const today = new Date().toISOString().slice(0, 10);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Group jobs by date
  const jobsByDate = {};
  jobs.forEach(j => {
    const d = j.scheduled_date;
    if (!jobsByDate[d]) jobsByDate[d] = [];
    jobsByDate[d].push(j);
  });

  const handleJobClick = (job) => {
    window.location.href = `/operations/field-jobs`;
  };

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="field-schedule-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">Field Schedule<PageInfoTooltip pageKey="field-schedule" /></h1>
          <p className="text-gray-medium text-sm mt-1">Calendar view of all scheduled field jobs</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={goToday}>Today</Button>
          <Button variant="outline" size="sm" onClick={fetchSchedule} disabled={loading}>
            <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Calendar */}
        <Card className="lg:col-span-3 bg-white border-gray-border" data-testid="calendar-card">
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={prevMonth} data-testid="prev-month">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <CardTitle className="text-base font-medium min-w-[180px] text-center">{formatMonth(year, month)}</CardTitle>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={nextMonth} data-testid="next-month">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <Badge variant="outline" className="text-xs">{jobs.length} jobs this month</Badge>
          </CardHeader>
          <CardContent className="p-2 sm:p-4">
            {/* Week day headers */}
            <div className="grid grid-cols-7 gap-px mb-1">
              {weekDays.map(d => (
                <div key={d} className="text-center text-[10px] font-medium text-gray-400 py-1">{d}</div>
              ))}
            </div>
            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-px">
              {/* Empty cells before first day */}
              {Array.from({ length: firstDay }).map((_, i) => (
                <div key={`empty-${i}`} className="h-20 sm:h-24 bg-gray-50/50 rounded" />
              ))}
              {/* Day cells */}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const dateStr = toDateStr(year, month, day);
                const dayJobs = jobsByDate[dateStr] || [];
                const isToday = dateStr === today;
                const isSelected = dateStr === selectedDate;
                return (
                  <div
                    key={day}
                    onClick={() => setSelectedDate(dateStr)}
                    className={cn(
                      "h-20 sm:h-24 p-1 rounded border cursor-pointer transition-all",
                      isToday ? "border-emerald-400 bg-emerald-50/30" : "border-transparent hover:border-gray-200",
                      isSelected && "ring-2 ring-emerald-500/40 border-emerald-300",
                      "hover:bg-gray-50/50"
                    )}
                    data-testid={`cal-day-${dateStr}`}
                  >
                    <div className="flex items-center justify-between mb-0.5">
                      <span className={cn(
                        "text-xs font-medium w-5 h-5 flex items-center justify-center rounded-full",
                        isToday ? "bg-emerald-500 text-white" : "text-gray-600"
                      )}>{day}</span>
                      {dayJobs.length > 0 && (
                        <span className="text-[9px] text-gray-400">{dayJobs.length}</span>
                      )}
                    </div>
                    <div className="space-y-0.5 overflow-hidden">
                      {dayJobs.slice(0, 3).map(job => (
                        <JobPill key={job.id} job={job} onClick={handleJobClick} />
                      ))}
                      {dayJobs.length > 3 && (
                        <p className="text-[9px] text-gray-400 pl-1">+{dayJobs.length - 3} more</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Day Detail Sidebar */}
        <div className="space-y-4">
          <DayDetail date={selectedDate} jobs={jobs} onClose={() => setSelectedDate(null)} onJobClick={handleJobClick} />

          {/* Legend */}
          <Card className="bg-white border-gray-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-gray-400">Status Legend</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {Object.entries(STATUS_COLORS).map(([status, classes]) => (
                <div key={status} className="flex items-center gap-2">
                  <div className={cn("w-3 h-3 rounded border", classes)} />
                  <span className="text-[11px] text-gray-500 capitalize">{status.replace('_', ' ')}</span>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Priority Legend */}
          <Card className="bg-white border-gray-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-gray-400">Priority</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {Object.entries(PRIORITY_DOTS).map(([p, c]) => (
                <div key={p} className="flex items-center gap-2">
                  <div className={cn("w-2 h-2 rounded-full", c)} />
                  <span className="text-[11px] text-gray-500 capitalize">{p}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
